import { httpClient } from '@bs/core'
import type {
  Review,
  ReviewListParams,
  ReviewListResponse,
  ReviewProductOption,
  ReviewRelatedProduct,
  ReviewStatus,
} from '../../types/review'

// Reuses the existing legacy comment endpoint as-is — the facelift is
// frontend-only, no backend rename. Mirrors
// frontend/apps/breadstack-crm/src/services/apis/comment.ts.
const REVIEWS_URL = '/stores/woocommerce/products/reviews/'
// Related-product lookups (filter dropdown + the detail dialog's product
// card) reuse the same WooCommerce products endpoint the legacy Comment
// Filter/RelatedProduct components called directly — not the pantry Product
// feature, which is POS-specific (cova/greenline/lightspeed/flowhub) and out
// of scope here.
const PRODUCTS_URL = '/stores/woocommerce/products/'

interface ApiReviewItem {
  id: number
  reviewer?: string
  reviewer_email?: string
  rating?: number
  review?: string
  status?: ReviewStatus
  product_id?: number
  product_name?: string
  // `product_permalink` is unreliable/always empty from this endpoint; the
  // storefront link is built client-side from `store.storeUrl` + this slug
  // (see MINE-0005).
  product_slug?: string
  date_created_gmt?: string
  customer_id?: number
}

interface PaginateResponse {
  items?: ApiReviewItem[]
  total_items?: number
  total_pages?: number
  page?: number
  // Per-status counts the endpoint has always returned. Used to derive the
  // list total when `total_items` is absent (older backend), so the pagination
  // footer works regardless of whether the `total_items` backend fix is live.
  count_under_review?: number
  count_published?: number
  count_spam?: number
  count_trash?: number
}

interface ApiProductItem {
  id: number
  name?: string
  permalink?: string
  images?: { src: string }[]
}

function mapFromApi(item: ApiReviewItem): Review {
  return {
    id: item.id,
    reviewer: item.reviewer ?? '',
    reviewerEmail: item.reviewer_email ?? '',
    rating: Number(item.rating ?? 0),
    review: item.review ?? '',
    status: item.status ?? 'hold',
    productId: item.product_id ?? 0,
    productName: item.product_name ?? '',
    productSlug: item.product_slug ?? '',
    dateCreatedGmt: item.date_created_gmt ?? '',
    customerId: item.customer_id,
  }
}

function mapToApi(review: Partial<Review> & { id: number }): Record<string, unknown> {
  return {
    id: review.id,
    reviewer: review.reviewer,
    rating: review.rating,
    review: review.review,
    status: review.status,
    product_id: review.productId,
    date_created_gmt: review.dateCreatedGmt,
    customer_id: review.customerId,
  }
}

export const reviewApis = {
  paginate: async (
    storeId: string,
    params: Partial<ReviewListParams>,
  ): Promise<ReviewListResponse> => {
    const { page, limit, search_parameter, product_id, status, rating, from, to, sort_by, sort } =
      params

    const filters: Record<string, unknown> = {
      status: status || { $nin: ['trash', 'spam'] },
    }
    if (product_id) filters.product_id = Number(product_id)
    if (rating) filters.rating = Number(rating)
    if (from || to) {
      filters.date_created_gmt = { from, to }
    }

    const parameters: Record<string, unknown> = {
      page: page || 1,
      limit: limit || 20,
      filters,
      sort: sort_by ? { [sort_by]: Number(sort) || -1 } : { status: -1 },
    }
    // `search_parameter` comes from a URL query param in legacy
    // CommentList — when the box is empty, the key simply isn't present in
    // `filtersFromLocation`. Mirror that: omit the key entirely rather than
    // sending an empty string.
    if (search_parameter) {
      parameters.search_parameter = search_parameter
    }

    const raw = (await httpClient.json.post(REVIEWS_URL, {
      store_id: storeId,
      action: 'paginate',
      parameters,
    })) as PaginateResponse

    const items = raw?.items ?? []

    // Prefer the backend's `total_items`; fall back to the per-status counts
    // (always present) so the footer still works if that field isn't live yet.
    // The "All" tab sends status `{$nin:[trash,spam]}` → total = hold+approved.
    const statusValue = typeof status === 'string' ? status : ''
    const derivedCount =
      statusValue === 'spam'
        ? raw?.count_spam
        : statusValue === 'trash'
          ? raw?.count_trash
          : statusValue === 'approved'
            ? raw?.count_published
            : statusValue === 'hold'
              ? raw?.count_under_review
              : (raw?.count_under_review ?? 0) + (raw?.count_published ?? 0)

    const totalItems = raw?.total_items ?? derivedCount ?? items.length
    const pageSize = limit || 20
    // Compute pages from the reliable total so an older backend's stale
    // `total_pages` (forced to 1 on the All tab) can't suppress the strip.
    const totalPages = totalItems > 0 ? Math.ceil(totalItems / pageSize) : 1

    return {
      data: items.map(mapFromApi),
      totalItems,
      totalPages,
      currentPage: raw?.page ?? page ?? 1,
    }
  },

  getDetail: async (
    storeId: string,
    id: number,
    productId: number,
  ): Promise<Review | null> => {
    const raw = (await httpClient.json.post(REVIEWS_URL, {
      store_id: storeId,
      action: 'read',
      parameters: { id, product_id: productId },
    })) as ApiReviewItem | null
    if (!raw) return null
    return mapFromApi(raw)
  },

  update: async (
    storeId: string,
    review: Partial<Review> & { id: number },
  ): Promise<Review> => {
    const raw = (await httpClient.json.post(REVIEWS_URL, {
      store_id: storeId,
      action: 'update',
      parameters: { item_data: mapToApi(review) },
    })) as ApiReviewItem
    return mapFromApi(raw)
  },

  updateStatus: async (
    storeId: string,
    review: Review,
    status: ReviewStatus,
  ): Promise<Review> => reviewApis.update(storeId, { ...review, status }),

  remove: async (storeId: string, id: number): Promise<void> => {
    await httpClient.json.post(REVIEWS_URL, {
      store_id: storeId,
      action: 'delete',
      parameters: { id },
    })
  },

  bulkUpdate: async (
    storeId: string,
    ids: number[],
    status: ReviewStatus,
  ): Promise<void> => {
    await httpClient.json.post(REVIEWS_URL, {
      store_id: storeId,
      action: 'bulk_update',
      parameters: { ids, status },
    })
  },

  bulkDelete: async (storeId: string, ids: number[]): Promise<void> => {
    await httpClient.json.post(REVIEWS_URL, {
      store_id: storeId,
      action: 'bulk_delete',
      parameters: { ids },
    })
  },

  searchProducts: async (
    storeId: string,
    keyword: string,
  ): Promise<ReviewProductOption[]> => {
    const raw = (await httpClient.json.post(PRODUCTS_URL, {
      store_id: storeId,
      action: 'paginate',
      parameters: {
        limit: 25,
        page: 1,
        search_parameter: keyword,
        filters: { status: { $ne: 'trash' }, type: { $ne: 'variation' } },
      },
    })) as { items?: ApiProductItem[] }
    const items = raw?.items ?? []
    return items.map((item) => ({ id: item.id, name: item.name ?? '' }))
  },

  getRelatedProduct: async (
    storeId: string,
    productId: number,
  ): Promise<ReviewRelatedProduct | null> => {
    const raw = (await httpClient.json.post(PRODUCTS_URL, {
      store_id: storeId,
      action: 'read',
      parameters: { id: productId },
    })) as ApiProductItem | null
    if (!raw) return null
    return {
      id: raw.id,
      name: raw.name ?? '',
      permalink: raw.permalink ?? '',
      image: raw.images && raw.images.length > 0 ? raw.images[0].src : undefined,
    }
  },
}
